import type { FC, PropsWithChildren } from "react"
import { useMemo, useReducer, useState } from "react"
import { useTranslation } from "react-i18next"
import { Responsive, WidthProvider } from "react-grid-layout"
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"
import { Box, Stack, Typography } from "@mui/material"
import fp from "lodash/fp"

import type { LatLngTuple } from "leaflet"

import type { QueryPlotDef } from "types/dashboard.types"
import type { Tower } from "types/towers.types"

import { GRID_LAYOUT_GAP, MD_ROW_HEIGHT, SM_ROW_HEIGHT } from "helpers/utils/constants"
import { layoutReducer } from "helpers/reducers/dashboardReducers"
import { getCellHeight, getLayout, sortPlotDefsByLayout } from "helpers/utils/dashboards"
import Message from "widgets/common/Message"
import HoverSyncProvider from "helpers/contexts/HoverSyncProvider"
import Map from "widgets/plots/geo/Map"
import { TOWER } from "helpers/utils/map"
import { PlotContainer } from "widgets/styled/containers"
import MapMarker from "widgets/plots/geo/MapMarker"
import GeoPopup from "widgets/plots/geo/GeoPopup"
import DashboardContext from "helpers/contexts/DashboardContext"
import useDateRange from "helpers/hooks/useDateRange"
import CrossEntityPlot from "widgets/plots/CrossEntityPlot"
import { nameOrUnnamed } from "helpers/utils/translations"

const GridLayout = WidthProvider(Responsive)

interface TowerDashboardProps {
  tower: Tower
  towerFetching: boolean
  config: {
    type: string
    plotDefs: QueryPlotDef[]
  }
}

const TowerDashboard: FC<PropsWithChildren<TowerDashboardProps>> = ({
  tower,
  towerFetching,
  config,
  children,
}) => {
  const { t } = useTranslation()
  const { arePlotsLoading } = useDateRange()
  const initialLayout = getLayout(config.type)
  //TODO: Once we have more layouts for the different media queries, we'll have to take that into account for the initial layout
  const [plotLayout, dispatchPlotLayout] = useReducer(layoutReducer, initialLayout)
  const plotDefs = useMemo(
    () => sortPlotDefsByLayout(config.plotDefs, initialLayout),
    [config.plotDefs, initialLayout],
  )
  const layout = useMemo(() => fp.values(plotLayout), [plotLayout])

  const [rowHeight, setRowHeight] = useState(MD_ROW_HEIGHT)

  // Only consider plots that are actually rendered. Some layouts define cells
  // for plots that are conditionally dropped from `plotDefs`; those orphaned
  // entries never collapse to h: 0, so checking the full layout would keep
  // `isData` permanently true and suppress the no-data message.
  const isData = config.plotDefs.some(
    (def) => (plotLayout[def.view.nameKey]?.h ?? 0) !== 0,
  )

  const { shouldRenderMap, position } = useMemo(() => {
    const { latitude, longitude, altitude } = tower.geo_location ?? {}
    const mapInLayout = config.plotDefs.some((def) => def.view.nameKey === "LOCATION")
    const validCoords = typeof latitude === "number" && typeof longitude === "number"
    return {
      shouldRenderMap: validCoords && mapInLayout,
      position: validCoords
        ? typeof altitude === "number"
          ? ([latitude, longitude, altitude] as LatLngTuple)
          : ([latitude, longitude] as LatLngTuple)
        : undefined,
    }
  }, [config.plotDefs, tower.geo_location])

  const mapHeight = useMemo(() => {
    return `calc(${getCellHeight("LOCATION", "legacyTowerInfoTab") * rowHeight * 0.85}px)`
  }, [rowHeight])

  return (
    <DashboardContext.Provider
      value={{
        config,
        entityType: "tower" as const,
        plotLayout,
        dispatchPlotLayout,
        rowHeight,
      }}
    >
      <HoverSyncProvider>
        {children}
        <GridLayout
          style={{
            position: "relative",
            marginRight: `-${GRID_LAYOUT_GAP}px`,
            marginLeft: `-${GRID_LAYOUT_GAP}px`,
          }}
          layouts={{ lg: layout }}
          breakpoints={{
            lg: 1000,
            md: 800,
            sm: 650,
          }}
          cols={{ lg: 12, md: 6, sm: 3 }}
          margin={[GRID_LAYOUT_GAP, GRID_LAYOUT_GAP]}
          rowHeight={rowHeight}
          draggableHandle=".dragIcon"
          onBreakpointChange={(breakpoint: string) =>
            breakpoint === "sm"
              ? setRowHeight(SM_ROW_HEIGHT)
              : setRowHeight(MD_ROW_HEIGHT)
          }
        >
          {plotDefs.map((def) => {
            if (def.view.nameKey === "LOCATION") {
              return (
                <div key="LOCATION">
                  {!towerFetching && shouldRenderMap && (
                    <PlotContainer maxWidth="xl" type={"line"}>
                      <Box style={{ display: "flex", justifyContent: "space-between" }}>
                        <Stack
                          direction={"row"}
                          gap={1}
                          alignItems={"center"}
                          pb={2}
                          height={40}
                        >
                          <Typography variant="h5">{t("towers.LOCATION")}</Typography>
                        </Stack>
                        <DragIndicatorIcon className="dragIcon" data-testid="drag" />
                      </Box>
                      <Map
                        key={`map${tower.id}${mapHeight}`}
                        center={position ?? undefined}
                        mapHeight={mapHeight}
                      >
                        <MapMarker
                          key={tower.id}
                          popup={
                            <GeoPopup
                              type="tower"
                              data={{ ...tower, kind: "PowerlineTower" }}
                              hideButton
                            />
                          }
                          position={position}
                          icon={TOWER}
                          zIndexOffset={0}
                          tooltipContent={nameOrUnnamed(tower.name, t)}
                        />
                      </Map>
                    </PlotContainer>
                  )}
                </div>
              )
            }
            return (
              <div key={def.view.nameKey}>
                <CrossEntityPlot plotDef={def} contextEntity={tower} />
              </div>
            )
          })}
        </GridLayout>
      </HoverSyncProvider>
      {!arePlotsLoading && !isData && !shouldRenderMap && (
        <Message messageKey={"generic.NO_DATA_IN_TIME_WINDOW"} />
      )}
    </DashboardContext.Provider>
  )
}

export default TowerDashboard
